import SalaryStructureManager from '../employee-management/salary-structure/SalaryStructureManager.jsx';
import StatutoryRatesManager from '../employee-management/salary-structure/StatutoryRatesManager.jsx';
import SalaryStructureSimulator from '../employee-management/salary-structure/SalaryStructureSimulator.jsx';
import { useState, useEffect } from 'react';
import { formatDate } from '../../../lib/employeeHelpers';
import { toast } from 'react-hot-toast';
import { X } from 'lucide-react';

// Enhanced Salary Structure Manager Component - Local Implementation
const EnhancedSalaryStructureManager = ({ darkMode, employees, departmentsList }) => {
    // Core state for the new salary structure system
    const [activeTab, setActiveTab] = useState('structures');
    const [templates, setTemplates] = useState([]);
    const [components, setComponents] = useState([]);
    const [statutoryConfigs, setStatutoryConfigs] = useState([]);
    const [structures, setStructures] = useState([]);
    const [versions, setVersions] = useState([]);
    const [scenarios, setScenarios] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [selectedStructure, setSelectedStructure] = useState(null);
    const [showFormulaBuilder, setShowFormulaBuilder] = useState(false);
    const [showComponentModal, setShowComponentModal] = useState(false);
    const [showComponentEditModal, setShowComponentEditModal] = useState(false);
    const [showStatutoryModal, setShowStatutoryModal] = useState(false);
    const [selectedComponent, setSelectedComponent] = useState(null);
    const [selectedStatutoryConfig, setSelectedStatutoryConfig] = useState(null);
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [showFormulaManager, setShowFormulaManager] = useState(false);
    const [savedFormulas, setSavedFormulas] = useState([]);
    const [selectedFormula, setSelectedFormula] = useState(null);
    const [verificationResult, setVerificationResult] = useState(null);
    const [showFormulaSelection, setShowFormulaSelection] = useState(false);
    const [selectedFormulaForComponent, setSelectedFormulaForComponent] = useState(null);
    const [simulatorData, setSimulatorData] = useState({
      targetAnnual: '',
      cityType: 'METRO',
      payableDaysMonthly: '',
      mode: 'CTC'
    });
    const [complianceReport, setComplianceReport] = useState([]);
  
    // Load saved data from localStorage
    useEffect(() => {
      const savedTemplates = localStorage.getItem('salaryTemplates');
      const savedComponents = localStorage.getItem('salaryComponents');
      const savedStatutoryConfigs = localStorage.getItem('salaryStatutoryConfigs');
      const savedStructures = localStorage.getItem('salaryStructures');
      const savedVersions = localStorage.getItem('salaryVersions');
      const savedScenarios = localStorage.getItem('salaryScenarios');
      const savedFormulas = localStorage.getItem('savedFormulas');
      
      if (savedTemplates) setTemplates(JSON.parse(savedTemplates));
      if (savedComponents) setComponents(JSON.parse(savedComponents));
      if (savedStatutoryConfigs) setStatutoryConfigs(JSON.parse(savedStatutoryConfigs));
      if (savedStructures) setStructures(JSON.parse(savedStructures));
      if (savedVersions) setVersions(JSON.parse(savedVersions));
      if (savedScenarios) setScenarios(JSON.parse(savedScenarios));
      if (savedFormulas) setSavedFormulas(JSON.parse(savedFormulas));
    }, []);
  
    // Handle click outside for dropdown
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (openDropdownId && !event.target.closest('.component-dropdown')) {
          setOpenDropdownId(null);
        }
      };
  
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [openDropdownId]);
  
    // Save functions for localStorage
    const saveTemplates = (newTemplates) => {
      setTemplates(newTemplates);
      localStorage.setItem('salaryTemplates', JSON.stringify(newTemplates));
    };
  
    const saveComponents = (newComponents) => {
      setComponents(newComponents);
      localStorage.setItem('salaryComponents', JSON.stringify(newComponents));
    };
  
    const saveStatutoryConfigs = (newConfigs) => {
      setStatutoryConfigs(newConfigs);
      localStorage.setItem('salaryStatutoryConfigs', JSON.stringify(newConfigs));
    };
  
    const saveStructures = (newStructures) => {
      setStructures(newStructures);
      localStorage.setItem('salaryStructures', JSON.stringify(newStructures));
    };
  
    const saveVersions = (newVersions) => {
      setVersions(newVersions);
      localStorage.setItem('salaryVersions', JSON.stringify(newVersions));
    };
  
    const saveScenarios = (newScenarios) => {
      setScenarios(newScenarios);
      localStorage.setItem('salaryScenarios', JSON.stringify(newScenarios));
    };
  
    const saveFormulas = (newFormulas) => {
      setSavedFormulas(newFormulas);
      localStorage.setItem('savedFormulas', JSON.stringify(newFormulas));
    };
  
    // Add new formula
    const addFormula = (formulaData) => {
      const newFormula = {
        id: Date.now(),
        name: formulaData.name,
        code: formulaData.code,
        formula: formulaData.formula,
        componentId: formulaData.componentId,
        category: formulaData.category || 'CUSTOM'
      };
      saveFormulas([...savedFormulas, newFormula]);
    };
  
    // Update formula
    const updateFormula = (id, updates) => {
      const updatedFormulas = savedFormulas.map(formula => 
        formula.id === id ? { ...formula, ...updates } : formula
      );
      saveFormulas(updatedFormulas);
    };
  
    // Delete formula
    const deleteFormula = (id) => {
      const filteredFormulas = savedFormulas.filter(formula => formula.id !== id);
      saveFormulas(filteredFormulas);
    };
  
    // Formula Manager helper functions
    const addToFormula = (text) => {
      setSelectedFormula(prev => ({
        ...prev,
        formula: (prev?.formula || '') + text
      }));
    };
  
    const verifyFormula = () => {
      try {
        const formula = selectedFormula?.formula || '';
        if (!formula.trim()) {
          setVerificationResult({ valid: false, error: 'Formula cannot be empty' });
          return;
        }
  
        // Create a safe evaluation environment
        const safeEval = (expr) => {
          // Replace component names with test values
          let testFormula = formula;
          const testValues = {
            'BASIC': 50000,
            'DA': 2500,
            'HRA': 20000,
            'GROSS_EARNINGS': 72500,
            'CTC': 100000,
            'PF_EMP': 6000,
            'PF_EMPR': 6000,
            'ESI_EMP': 543,
            'ESI_EMPR': 2356
          };
  
          // Add component test values
          components.forEach(comp => {
            testValues[comp.code || comp.name] = 1000; // Default test value
          });
  
          // Replace all variables with test values
          Object.keys(testValues).forEach(key => {
            const regex = new RegExp(`\\b${key}\\b`, 'g');
            testFormula = testFormula.replace(regex, testValues[key]);
          });
  
          // Basic validation - only allow safe operations
          const allowedChars = /^[0-9+\-*/().\s]+$/;
          if (!allowedChars.test(testFormula)) {
            throw new Error('Formula contains invalid characters');
          }
  
          // Evaluate the formula
          const result = eval(testFormula);
          return isFinite(result) ? result : 0;
        };
  
        const result = safeEval(formula);
        setVerificationResult({ 
          valid: true, 
          result: result,
          message: `Formula is valid. Test result: ₹${result.toFixed(2)}`
        });
      } catch (error) {
        setVerificationResult({ 
          valid: false, 
          error: `Formula error: ${error.message}` 
        });
      }
    };
  
    const saveFormula = () => {
      if (!selectedFormula?.name || !selectedFormula?.formula) {
        toast.error('Please provide both name and formula');
        return;
      }
  
      if (!verificationResult?.valid) {
        toast.error('Please verify the formula before saving');
        return;
      }
  
      if (selectedFormula.id) {
        // Update existing formula
        updateFormula(selectedFormula.id, {
          name: selectedFormula.name,
          code: selectedFormula.code,
          formula: selectedFormula.formula
        });
      } else {
        // Add new formula
        addFormula({
          name: selectedFormula.name,
          code: selectedFormula.code,
          formula: selectedFormula.formula,
          componentId: null,
          category: 'CUSTOM'
        });
      }
  
      setSelectedFormula(null);
      setVerificationResult(null);
      toast.success('Formula saved successfully!');
    };
  
    // Add new component with enhanced structure
    const addComponent = () => {
      const newComponent = {
        id: Date.now(),
        name: '',
        code: '',
        category: 'EARNING', // EARNING, DEDUCTION, EMPLOYER_BENEFIT
        nature: 'FIXED', // FIXED, VARIABLE, STATUTORY, PERQUISITE, ONE_TIME
        calculationType: 'FIXED', // FIXED, PERCENT_OF_BASIC, PERCENT_OF_GROSS, PERCENT_OF_CTC, FORMULA, SLAB, PRO_RATA, BALANCING
        valueExpression: '',
        frequency: 'MONTHLY', // MONTHLY, ANNUAL, QUARTERLY
        flags: {
          pfApplicable: false,
          esiApplicable: false,
          taxable: 'TAXABLE' // TAXABLE, EXEMPT, PARTIAL
        },
        notes: '',
        isActive: true
      };
      saveComponents([...components, newComponent]);
      
      // Add component to formula manager
      const newFormula = {
        id: Date.now(),
        name: newComponent.name || 'Unnamed Component',
        code: newComponent.code || '',
        formula: newComponent.valueExpression || '',
        componentId: newComponent.id,
        category: 'COMPONENT'
      };
      saveFormulas([...savedFormulas, newFormula]);
    };
  
    // Update component
    const updateComponent = (id, updates) => {
      const updatedComponents = components.map(comp => 
        comp.id === id ? { ...comp, ...updates } : comp
      );
      saveComponents(updatedComponents);
    };
  
    // Delete component
    const deleteComponent = (id) => {
      const filteredComponents = components.filter(comp => comp.id !== id);
      saveComponents(filteredComponents);
    };
  
    // Add new template
    const addTemplate = () => {
      const newTemplate = {
        id: Date.now(),
        name: 'New Template',
        mode: 'CTC', // CTC, GROSS
        components: [],
        statutoryConfigId: null,
        defaultCityType: 'METRO', // METRO, NON_METRO
        active: true
      };
      saveTemplates([...templates, newTemplate]);
    };
  
    // Update template
    const updateTemplate = (id, updates) => {
      const updatedTemplates = templates.map(template => 
        template.id === id ? { ...template, ...updates } : template
      );
      saveTemplates(updatedTemplates);
    };
  
    // Delete template
    const deleteTemplate = (id) => {
      const filteredTemplates = templates.filter(template => template.id !== id);
      saveTemplates(filteredTemplates);
    };
  
    // Add new statutory config
    const addStatutoryConfig = () => {
      const newConfig = {
        id: Date.now(),
        name: 'Default Config',
        pf: {
          employeePct: 12,
          employerPct: 12,
          wageCeiling: 15000,
          applyCeiling: true,
          voluntaryAboveCeiling: false,
          wageDefinition: 'BASIC_DA'
        },
        esi: {
          employeePct: 0.75,
          employerPct: 3.25,
          eligibilityGrossMonthly: 21000
        },
        gratuity: {
          basis: 'BASIC',
          ratePct: 4.81
        },
        bonus: {
          eligibilityMonthlyWage: 21000,
          minPct: 8.33,
          maxPct: 20,
          wageDefinition: 'STATUTORY_WAGE'
        },
        pt: {
          state: '',
          slabs: {}
        },
        lwf: {
          state: '',
          employeeAmt: 0,
          employerAmt: 0,
          periodicity: 'MONTHLY'
        },
        labourCodeRule: {
          minBasicPlusDAFractionOfWages: 0.5
        },
        minimumWage: {
          enabled: false,
          tableRef: null
        }
      };
      saveStatutoryConfigs([...statutoryConfigs, newConfig]);
    };
  
    // Update statutory config
    const updateStatutoryConfig = (id, updates) => {
      const updatedConfigs = statutoryConfigs.map(config => 
        config.id === id ? { ...config, ...updates } : config
      );
      saveStatutoryConfigs(updatedConfigs);
    };
  
    // Delete statutory config
    const deleteStatutoryConfig = (id) => {
      const filteredConfigs = statutoryConfigs.filter(config => config.id !== id);
      saveStatutoryConfigs(filteredConfigs);
    };
  
    // Add new structure
    const addStructure = () => {
      const newStructure = {
        id: Date.now(),
        templateId: null,
        title: '',
        effectiveFrom: '',
        mode: 'CTC', // CTC, GROSS
        targetAnnual: 0,
        parameters: {
          cityType: 'METRO',
          payableDaysMonthly: null
        },
        resolvedComponents: [],
        computedSummary: {
          monthlyGross: 0,
          annualGross: 0,
          employerContrib: 0,
          annualCTC: 0,
          notes: []
        },
        complianceReport: [],
        versionInfoId: null
      };
      saveStructures([...structures, newStructure]);
    };
  
    // Update structure
    const updateStructure = (id, updates) => {
      const updatedStructures = structures.map(structure => 
        structure.id === id ? { ...structure, ...updates } : structure
      );
      saveStructures(updatedStructures);
    };
  
    // Delete structure
    const deleteStructure = (id) => {
      const filteredStructures = structures.filter(structure => structure.id !== id);
      saveStructures(filteredStructures);
    };
  
    // Enhanced calculation engine
    const calculateSalary = (structure, targetAnnual, parameters = {}, statutoryConfig = null) => {
      try {
        // Sort components by calculation order
        const sortedComponents = [...structure.resolvedComponents].sort((a, b) => a.ordering - b.ordering);
        
        let basic = 0;
        let da = 0;
        let hra = 0;
        let grossEarnings = 0;
        let netEarnings = 0;
        let employerBenefits = 0;
        
        // Calculate components in order
        sortedComponents.forEach(component => {
          let value = 0;
          
          switch (component.calculationType) {
            case 'FIXED':
              value = parseFloat(component.valueExpression) || 0;
              break;
            case 'PERCENT_OF_BASIC':
              value = (basic * parseFloat(component.valueExpression)) / 100;
              break;
            case 'PERCENT_OF_GROSS':
              value = (grossEarnings * parseFloat(component.valueExpression)) / 100;
              break;
            case 'PERCENT_OF_CTC':
              value = (targetAnnual * parseFloat(component.valueExpression)) / 100;
              break;
            case 'FORMULA':
              try {
                // Replace tokens with actual values
                let formula = component.valueExpression;
                formula = formula.replace(/BASIC/g, basic);
                formula = formula.replace(/DA/g, da);
                formula = formula.replace(/HRA/g, hra);
                formula = formula.replace(/GROSS_EARNINGS/g, grossEarnings);
                formula = formula.replace(/CTC/g, targetAnnual);
                value = eval(formula);
              } catch (error) {
                console.error('Formula evaluation error:', error);
                value = 0;
              }
              break;
            case 'BALANCING':
              // Calculate balancing amount
              if (structure.mode === 'CTC') {
                value = targetAnnual - grossEarnings - employerBenefits;
              } else {
                value = targetAnnual - grossEarnings;
              }
              break;
            default:
              value = 0;
          }
          
          // Apply pro-rata if applicable
          if (parameters.payableDaysMonthly && component.flags?.proratable) {
            value = (value * parameters.payableDaysMonthly) / 30;
          }
          
          // Update running totals
          if (component.category === 'EARNING') {
            grossEarnings += value;
            if (component.code === 'BASIC') basic = value;
            if (component.code === 'DA') da = value;
            if (component.code === 'HRA') hra = value;
          } else if (component.category === 'DEDUCTION') {
            netEarnings = grossEarnings - value;
          } else if (component.category === 'EMPLOYER_BENEFIT') {
            employerBenefits += value;
          }
          
          // Update component with calculated value
          component.calculatedValue = value;
        });
        
        // Calculate statutory components if config provided
        let statutory = {};
        if (statutoryConfig) {
          // PF calculation
          const pfWage = basic + da;
          const pfCeiling = statutoryConfig.pf.wageCeiling;
          const pfWageForCalc = Math.min(pfWage, pfCeiling);
          statutory.pfEmployee = (pfWageForCalc * statutoryConfig.pf.employeePct) / 100;
          statutory.pfEmployer = (pfWageForCalc * statutoryConfig.pf.employerPct) / 100;
          
          // ESI calculation
          if (grossEarnings <= statutoryConfig.esi.eligibilityGrossMonthly) {
            statutory.esiEmployee = (grossEarnings * statutoryConfig.esi.employeePct) / 100;
            statutory.esiEmployer = (grossEarnings * statutoryConfig.esi.employerPct) / 100;
          }
          
          // Gratuity calculation (annualized)
          statutory.gratuity = (basic * statutoryConfig.gratuity.ratePct * 12) / 100;
        }
        
        return {
          summary: {
            monthlyGross: grossEarnings,
            annualGross: grossEarnings * 12,
            employerContrib: employerBenefits + (statutory.pfEmployer || 0) + (statutory.esiEmployer || 0) + (statutory.gratuity || 0),
            annualCTC: targetAnnual,
            netSalary: netEarnings,
            notes: []
          },
          statutory,
          components: sortedComponents
        };
      } catch (error) {
        console.error('Salary calculation error:', error);
        return {
          summary: {
            monthlyGross: 0,
            annualGross: 0,
            employerContrib: 0,
            annualCTC: targetAnnual,
            netSalary: 0,
            notes: ['Calculation error occurred']
          },
          statutory: {},
          components: []
        };
      }
    };
  
    // Compliance check function
    const runComplianceCheck = (structure, calculatedSalary, statutoryConfig) => {
      const findings = [];
      
      // Basic+DA ≥ 50% of Wages rule
      const basic = calculatedSalary.components.find(c => c.code === 'BASIC')?.calculatedValue || 0;
      const da = calculatedSalary.components.find(c => c.code === 'DA')?.calculatedValue || 0;
      const wages = calculatedSalary.summary.monthlyGross;
      const basicPlusDA = basic + da;
      
      if (basicPlusDA < wages * 0.5) {
        findings.push({
          ruleId: 'BASIC_DA_50_PERCENT',
          severity: 'ERROR',
          message: `Basic+DA (${basicPlusDA}) is less than 50% of wages (${wages * 0.5})`,
          suggestedFix: {
            type: 'AUTO_ADJUST_BASDA',
            previewDelta: wages * 0.5 - basicPlusDA
          }
        });
      }
      
      // PF compliance
      if (statutoryConfig) {
        const pfWage = basic + da;
        const pfCeiling = statutoryConfig.pf.wageCeiling;
        
        if (pfWage > pfCeiling && !statutoryConfig.pf.voluntaryAboveCeiling) {
          findings.push({
            ruleId: 'PF_CEILING_VIOLATION',
            severity: 'WARN',
            message: `PF wage (${pfWage}) exceeds ceiling (${pfCeiling})`,
            suggestedFix: {
              type: 'ENFORCE_PF_CEILING',
              previewDelta: pfWage - pfCeiling
            }
          });
        }
      }
      
      return findings;
    };
  
    // Save version function
    const saveVersion = (structureId, changeNote) => {
      const structure = structures.find(s => s.id === structureId);
      if (structure) {
        const newVersion = {
          id: Date.now(),
          structureId,
          version: `v${versions.filter(v => v.structureId === structureId).length + 1}`,
          changeNote,
          changedBy: null,
          changedAt: new Date().toISOString(),
          structureSnapshot: { ...structure }
        };
        saveVersions([...versions, newVersion]);
        return newVersion;
      }
    };
  
    // Create scenario function
    const createScenario = (structureId, name, overrides) => {
      const structure = structures.find(s => s.id === structureId);
      if (structure) {
        const newScenario = {
          id: Date.now(),
          structureId,
          name,
          overrides,
          createdAt: new Date().toISOString(),
          comparisonSummary: null
        };
        saveScenarios([...scenarios, newScenario]);
        return newScenario;
      }
    };
  
    // Formula Builder Component
    const FormulaBuilder = ({ isOpen, onClose, component, onSave }) => {
      const [formula, setFormula] = useState(component?.valueExpression || '');
      const [availableComponents, setAvailableComponents] = useState([]);
      const [verificationResult, setVerificationResult] = useState(null);
      const [testValue, setTestValue] = useState('100000'); // Default CTC for testing
  
      useEffect(() => {
        // Get all components including standard tokens
        const allComponents = [
          { name: 'BASIC', type: 'token', value: '50000' },
          { name: 'DA', type: 'token', value: '2500' },
          { name: 'HRA', type: 'token', value: '20000' },
          { name: 'GROSS_EARNINGS', type: 'token', value: '72500' },
          { name: 'CTC', type: 'token', value: testValue },
          { name: 'PF_EMP', type: 'token', value: '6000' },
          { name: 'PF_EMPR', type: 'token', value: '6000' },
          { name: 'ESI_EMP', type: 'token', value: '543' },
          { name: 'ESI_EMPR', type: 'token', value: '2356' },
          ...components.filter(comp => comp.id !== component?.id).map(comp => ({
            name: comp.code || comp.name,
            type: 'component',
            value: comp.valueExpression || '0'
          }))
        ];
        setAvailableComponents(allComponents);
      }, [component, components, testValue]);
  
      const addToFormula = (text) => {
        setFormula(prev => prev + text);
      };
  
      const verifyFormula = () => {
        try {
          if (!formula.trim()) {
            setVerificationResult({ valid: false, error: 'Formula cannot be empty' });
            return;
          }
  
          // Create a safe evaluation environment
          const safeEval = (expr) => {
            // Replace component names with test values
            let testFormula = formula;
            availableComponents.forEach(comp => {
              const regex = new RegExp(`\\b${comp.name}\\b`, 'g');
              testFormula = testFormula.replace(regex, comp.value || '0');
            });
  
            // Basic validation - only allow safe operations
            const allowedChars = /^[0-9+\-*/().\s]+$/;
            if (!allowedChars.test(testFormula)) {
              throw new Error('Formula contains invalid characters');
            }
  
            // Evaluate the formula
            const result = eval(testFormula);
            return isFinite(result) ? result : 0;
          };
  
          const result = safeEval(formula);
          setVerificationResult({ 
            valid: true, 
            result: result,
            message: `Formula is valid. Test result: ₹${result.toFixed(2)}`
          });
        } catch (error) {
          setVerificationResult({ 
            valid: false, 
            error: `Formula error: ${error.message}` 
          });
        }
      };
  
      const handleSave = () => {
        if (!verificationResult?.valid) {
          toast.error('Please verify the formula before saving');
          return;
        }
        onSave(formula);
        onClose();
      };
  
      if (!isOpen) return null;
  
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`p-6 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                Advanced Formula Builder
              </h3>
              <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>
  
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Formula Building */}
              <div className="space-y-4">
                {/* Available Components */}
                <div>
                  <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Available Components & Variables
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {availableComponents.map(comp => (
                      <button
                        key={comp.name}
                        onClick={() => addToFormula(comp.name)}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          comp.type === 'token' 
                            ? (darkMode ? 'bg-redhalo-navy text-white hover:bg-redhalo-navy-light' : 'rh-accent-soft hover:bg-redhalo-mint')
                            : (darkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-100 text-green-700 hover:bg-green-200')
                        } transition-colors`}
                        title={`${comp.name}: ${comp.value || '0'}`}
                      >
                        {comp.name}
                      </button>
                    ))}
                  </div>
                </div>
  
                {/* Operators */}
                <div>
                  <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Operators
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {['+', '-', '*', '/', '(', ')', '%'].map(op => (
                      <button
                        key={op}
                        onClick={() => addToFormula(op)}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          darkMode ? 'bg-gray-600 text-white hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        } transition-colors`}
                      >
                        {op}
                      </button>
                    ))}
                  </div>
                </div>
  
                {/* Functions */}
                <div>
                  <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Functions
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {['Math.round()', 'Math.floor()', 'Math.ceil()', 'Math.min()', 'Math.max()'].map(func => (
                      <button
                        key={func}
                        onClick={() => addToFormula(func)}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          darkMode ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                        } transition-colors`}
                      >
                        {func}
                      </button>
                    ))}
                  </div>
                </div>
  
                {/* Formula Input */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Formula
                  </label>
                  <textarea
                    value={formula}
                    onChange={(e) => setFormula(e.target.value)}
                    className={`w-full p-3 border rounded text-sm ${darkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    rows={6}
                    placeholder="Enter formula (e.g., CTC * 0.4 + Basic * 0.1)"
                  />
                </div>
  
                {/* Test Value */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Test CTC Value
                  </label>
                  <input
                    type="number"
                    value={testValue}
                    onChange={(e) => setTestValue(e.target.value)}
                    className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    placeholder="Enter test CTC value"
                  />
                </div>
              </div>
  
              {/* Right Column - Verification & Preview */}
              <div className="space-y-4">
                {/* Verification */}
                <div>
                  <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Formula Verification
                  </h4>
                  <button
                    onClick={verifyFormula}
                    className={`w-full px-4 py-2 rounded text-sm font-medium ${
                      darkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-500 text-white hover:bg-green-600'
                    } transition-colors`}
                  >
                    Verify Formula
                  </button>
                  
                  {verificationResult && (
                    <div className={`mt-3 p-3 rounded ${
                      verificationResult.valid 
                        ? (darkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800')
                        : (darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800')
                    }`}>
                      <p className="text-sm font-medium">
                        {verificationResult.valid ? '✓ Valid' : '✗ Invalid'}
                      </p>
                      <p className="text-xs mt-1">
                        {verificationResult.valid ? verificationResult.message : verificationResult.error}
                      </p>
                    </div>
                  )}
                </div>
  
                {/* Preview */}
                <div>
                  <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Formula Preview
                  </h4>
                  <div className={`p-3 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <p className={`text-sm font-mono ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {formula || 'No formula entered'}
                    </p>
                  </div>
                </div>
  
                {/* Component Dependencies */}
                <div>
                  <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Component Dependencies
                  </h4>
                  <div className={`p-3 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    {availableComponents.filter(comp => formula.includes(comp.name)).length > 0 ? (
                      <div className="space-y-1">
                        {availableComponents
                          .filter(comp => formula.includes(comp.name))
                          .map(comp => (
                            <div key={comp.name} className="flex justify-between text-xs">
                              <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                {comp.name}
                              </span>
                              <span className={`font-mono ${darkMode ? 'text-gray-400' : 'text-gray-700'}`}>
                                ₹{comp.value || '0'}
                              </span>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        No dependencies found
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
  
            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={onClose}
                className={`px-4 py-2 rounded text-sm font-medium ${
                  darkMode ? 'bg-gray-600 text-white hover:bg-gray-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                } transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!verificationResult?.valid}
                className={`px-4 py-2 rounded text-sm font-medium ${
                  !verificationResult?.valid
                    ? `${darkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-300 text-gray-500'} cursor-not-allowed`
                    : `${darkMode ? 'bg-redhalo-navy text-white hover:bg-redhalo-navy-light' : 'bg-redhalo-red text-white hover:bg-redhalo-red-dark'}`
                } transition-colors`}
              >
                Save Formula
              </button>
            </div>
          </div>
        </div>
      );
    };
  
    // Template Tab
    const renderTemplateTab = () => (
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-md`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Salary Structure Templates
          </h2>
          <button
            onClick={addTemplate}
            className={`px-4 py-2 rounded text-sm font-medium ${
              darkMode ? 'bg-redhalo-navy text-white hover:bg-redhalo-navy-light' : 'bg-redhalo-red text-white hover:bg-redhalo-red-dark'
            } transition-colors`}
          >
            Create Template
          </button>
        </div>
  
        {/* Preset Templates */}
        <div className="mb-6">
          <h3 className={`text-md font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Preset Templates
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => {
                const entryTemplate = {
                  id: Date.now(),
                  name: 'Entry Level Template',
                  mode: 'CTC',
                  components: [
                    { code: 'BASIC', calculationType: 'PERCENT_OF_CTC', valueExpression: '45' },
                    { code: 'HRA', calculationType: 'PERCENT_OF_BASIC', valueExpression: '40' },
                    { code: 'DA', calculationType: 'PERCENT_OF_BASIC', valueExpression: '5' },
                    { code: 'SPECIAL_ALLOWANCE', calculationType: 'BALANCING', valueExpression: '' }
                  ],
                  defaultCityType: 'NON_METRO',
                  active: true
                };
                saveTemplates([...templates, entryTemplate]);
              }}
              className={`p-4 rounded-lg border text-left ${
                darkMode ? 'border-gray-600 bg-gray-700 hover:bg-gray-600' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
              } transition-colors`}
            >
              <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Entry Level</h4>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Basic 45% of CTC, HRA 40% of Basic
              </p>
            </button>
  
            <button
              onClick={() => {
                const midTemplate = {
                  id: Date.now(),
                  name: 'Mid Level Template',
                  mode: 'CTC',
                  components: [
                    { code: 'BASIC', calculationType: 'PERCENT_OF_CTC', valueExpression: '50' },
                    { code: 'HRA', calculationType: 'PERCENT_OF_BASIC', valueExpression: '40' },
                    { code: 'DA', calculationType: 'PERCENT_OF_BASIC', valueExpression: '5' },
                    { code: 'SPECIAL_ALLOWANCE', calculationType: 'BALANCING', valueExpression: '' }
                  ],
                  defaultCityType: 'NON_METRO',
                  active: true
                };
                saveTemplates([...templates, midTemplate]);
              }}
              className={`p-4 rounded-lg border text-left ${
                darkMode ? 'border-gray-600 bg-gray-700 hover:bg-gray-600' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
              } transition-colors`}
            >
              <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Mid Level</h4>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Basic 50% of CTC, HRA 40% of Basic
              </p>
            </button>
  
            <button
              onClick={() => {
                const seniorTemplate = {
                  id: Date.now(),
                  name: 'Senior Level Template',
                  mode: 'CTC',
                  components: [
                    { code: 'BASIC', calculationType: 'PERCENT_OF_CTC', valueExpression: '50' },
                    { code: 'HRA', calculationType: 'PERCENT_OF_BASIC', valueExpression: '50' },
                    { code: 'DA', calculationType: 'PERCENT_OF_BASIC', valueExpression: '10' },
                    { code: 'SPECIAL_ALLOWANCE', calculationType: 'BALANCING', valueExpression: '' }
                  ],
                  defaultCityType: 'METRO',
                  active: true
                };
                saveTemplates([...templates, seniorTemplate]);
              }}
              className={`p-4 rounded-lg border text-left ${
                darkMode ? 'border-gray-600 bg-gray-700 hover:bg-gray-600' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
              } transition-colors`}
            >
              <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Senior Level</h4>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Basic 50% of CTC, HRA 50% of Basic (Metro)
              </p>
            </button>
          </div>
        </div>
  
        {/* Custom Templates */}
        <div>
          <h3 className={`text-md font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Custom Templates
          </h3>
          <div className="space-y-4">
            {templates.map(template => (
              <div key={template.id} className={`p-4 rounded-lg border ${darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className={`text-md font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      {template.name}
                    </h4>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Mode: {template.mode} | City: {template.defaultCityType}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setSelectedTemplate(template)}
                      className={`px-3 py-1 rounded text-xs font-medium ${
                        darkMode ? 'bg-redhalo-navy text-white hover:bg-redhalo-navy-light' : 'bg-redhalo-red text-white hover:bg-redhalo-red-dark'
                      } transition-colors`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteTemplate(template.id)}
                      className={`px-3 py-1 rounded text-xs font-medium ${
                        darkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-500 text-white hover:bg-red-600'
                      } transition-colors`}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
  
            {templates.length === 0 && (
              <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                No custom templates created yet. Click "Create Template" or use preset templates to get started.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  
    // Components Tab
    const renderComponentsTab = () => (
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-md`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Salary Components
          </h2>
        </div>

        {/* Layout with Sidebar and Main Content */}
        <div className="flex gap-4">
          {/* Left Sidebar - Components List */}
          <div className={`w-80 flex-shrink-0 flex flex-col ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} p-4 rounded-none`}>
            {/* Add Component Button */}
            <button
              onClick={() => setShowComponentModal(true)}
              className={`w-full px-4 py-2 rounded-none text-sm font-medium mb-4 flex-shrink-0 ${
                darkMode ? 'bg-redhalo-navy text-white hover:bg-redhalo-navy-light' : 'bg-redhalo-red text-white hover:bg-redhalo-red-dark'
              } transition-colors`}
            >
              Add Component
            </button>

            {/* Components List - Fixed height with internal scroll */}
            <div className="flex-1 min-h-0 overflow-hidden">
              <div className="h-full overflow-y-auto pr-2 space-y-2">
                {components.length === 0 ? (
                  <div className={`text-center py-8 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    No components created yet.
                  </div>
                ) : (
                  components.map(component => (
                    <div
                      key={component.id}
                      className={`p-3 rounded-none border flex-shrink-0 ${darkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'} hover:shadow-md transition-shadow`}
                    >
                      {/* Component Name and Category */}
                      <div className="mb-2">
                        <h3 className={`font-semibold text-sm mb-1 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                          {component.name || 'Unnamed Component'}
                        </h3>
                        <span className={`px-2 py-1 rounded-none text-xs font-medium inline-block ${
                          component.category === 'EARNING' ? (darkMode ? 'bg-green-600 text-white' : 'bg-green-100 text-green-800') :
                          component.category === 'DEDUCTION' ? (darkMode ? 'bg-red-600 text-white' : 'bg-red-100 text-red-800') :
                          component.category === 'BENEFIT' ? (darkMode ? 'bg-redhalo-navy text-white' : 'rh-accent-soft') :
                          (darkMode ? 'bg-redhalo-navy text-white' : 'rh-accent-soft')
                        }`}>
                          {component.category}
                        </span>
                        {!component.isActive && (
                          <span className={`ml-2 px-2 py-1 rounded-none text-xs font-medium inline-block ${darkMode ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-800'}`}>
                            Inactive
                          </span>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-1 mt-2">
                        <button
                          onClick={() => {
                            setSelectedComponent(component);
                            setShowComponentEditModal(true);
                          }}
                          className={`px-2 py-1 rounded-none text-xs font-medium ${
                            darkMode ? 'bg-redhalo-navy text-white hover:bg-redhalo-navy-light' : 'bg-redhalo-red text-white hover:bg-redhalo-red-dark'
                          } transition-colors`}
                        >
                          Edit
                        </button>
                        {component.isActive ? (
                          <button
                            onClick={() => updateComponent(component.id, { isActive: false })}
                            className={`px-2 py-1 rounded-none text-xs font-medium ${
                              darkMode ? 'bg-yellow-600 text-white hover:bg-yellow-700' : 'bg-yellow-500 text-white hover:bg-yellow-600'
                            } transition-colors`}
                          >
                            Deactivate
                          </button>
                        ) : (
                          <button
                            onClick={() => updateComponent(component.id, { isActive: true })}
                            className={`px-2 py-1 rounded-none text-xs font-medium ${
                              darkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-500 text-white hover:bg-green-600'
                            } transition-colors`}
                          >
                            Reactivate
                          </button>
                        )}
                        <button
                          onClick={() => deleteComponent(component.id)}
                          className={`px-2 py-1 rounded-none text-xs font-medium ${
                            darkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-500 text-white hover:bg-red-600'
                          } transition-colors`}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1">
            {components.length === 0 ? (
              <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <p className="text-lg mb-2">No components created yet.</p>
                <p className="text-sm">Click "Add Component" to get started.</p>
              </div>
            ) : (
              <div className={`p-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-none`}>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Select a component from the sidebar to view details or use the action buttons to manage components.
                </p>
              </div>
            )}
          </div>
        </div>
  
        {/* Component Creation Modal */}
        {showComponentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className={`p-4 rounded-none shadow-xl max-w-4xl w-full ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`}>
              <div className="flex justify-between items-center mb-4">
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Create New Component
                </h3>
                <button
                  onClick={() => setShowComponentModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              </div>
  
              <div className="space-y-3">
                <div className="space-y-3">
                  {/* Component Name */}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Component Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="componentName"
                      className={`w-full p-2 border rounded-none text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                      placeholder="e.g., Basic Salary, HRA, DA"
                    />
                  </div>

                  {/* Category - Radio */}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Category <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: 'categoryEarning', label: 'Earning', value: 'EARNING' },
                        { id: 'categoryDeduction', label: 'Deduction', value: 'DEDUCTION' },
                        { id: 'categoryBenefit', label: 'Benefit', value: 'BENEFIT' },
                        { id: 'categoryReimbursement', label: 'Reimbursement', value: 'REIMBURSEMENT' },
                      ].map(opt => (
                        <label key={opt.id} className={`flex items-center px-2 py-2 border rounded-none ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <input type="radio" name="componentCategory" id={opt.id} value={opt.value} className="mr-2" />
                          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Notes
                    </label>
                    <textarea
                      id="componentNotes"
                      rows={3}
                      className={`w-full p-2 border rounded-none text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                      placeholder="Additional notes about this component..."
                    />
                  </div>
                </div>

              </div>
  
              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowComponentModal(false);
                    setShowFormulaSelection(false);
                    setSelectedFormulaForComponent(null);
                  }}
                  className={`px-4 py-2 rounded-none text-sm font-medium ${
                    darkMode ? 'bg-gray-600 text-white hover:bg-gray-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  } transition-colors`}
                >
                  Cancel
                </button>
                                <button
                    onClick={() => {
                      const name = document.getElementById('componentName').value.trim();
                      
                      // Validation
                      if (!name) {
                        toast.error('Please enter component name');
                        return;
                      }
                      
                      // Validate category (at least one must be selected)
                      const categoryValue = document.querySelector('input[name="componentCategory"]:checked')?.value;
                      if (!categoryValue) {
                        toast.error('Please select at least one category');
                        return;
                      }
  
                      const newComponent = {
                        id: Date.now(),
                        name,
                        code: name.toUpperCase().replace(/\s+/g, '_'),
                        category: categoryValue,
                        nature: 'FIXED',
                        calculationType: 'FIXED',
                        valueExpression: '',
                        frequency: 'MONTHLY',
                        flags: {
                          pfApplicable: false,
                          esiApplicable: false,
                          gratuityApplicable: false,
                          taxable: 'TAXABLE'
                        },
                        notes: '',
                        isActive: true
                      };
  
                      saveComponents([...components, newComponent]);
                      setShowComponentModal(false);
                      
                      // Clear form
                      document.getElementById('componentName').value = '';
                      document.querySelectorAll('input[name="componentCategory"]').forEach(el => (el.checked = false));
                      
                      toast.success('Component created successfully!');
                    }}
                  className={`px-4 py-2 rounded-none text-sm font-medium ${
                    darkMode ? 'bg-redhalo-navy text-white hover:bg-redhalo-navy-light' : 'bg-redhalo-red text-white hover:bg-redhalo-red-dark'
                  } transition-colors`}
                >
                  Create Component
                </button>
              </div>
            </div>
          </div>
        )}
  
        {/* Component Edit Modal */}
        {showComponentEditModal && selectedComponent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className={`p-4 rounded-none shadow-xl max-w-4xl w-full ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`}>
              <div className="flex justify-between items-center mb-4">
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Edit Component
                </h3>
                <button
                  onClick={() => {
                    setShowComponentEditModal(false);
                    setSelectedComponent(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-3">
                <div className="space-y-3">
                  {/* Component Name */}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Component Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="editComponentName"
                      defaultValue={selectedComponent.name}
                      className={`w-full p-2 border rounded-none text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                      placeholder="e.g., Basic Salary, HRA, DA"
                    />
                  </div>

                  {/* Category - Radio */}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Category <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: 'editCategoryEarning', label: 'Earning', value: 'EARNING' },
                        { id: 'editCategoryDeduction', label: 'Deduction', value: 'DEDUCTION' },
                        { id: 'editCategoryBenefit', label: 'Benefit', value: 'BENEFIT' },
                        { id: 'editCategoryReimbursement', label: 'Reimbursement', value: 'REIMBURSEMENT' },
                      ].map(opt => (
                        <label key={opt.id} className={`flex items-center px-2 py-2 border rounded-none ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <input
                            type="radio"
                            name="editComponentCategory"
                            id={opt.id}
                            value={opt.value}
                            defaultChecked={selectedComponent.category === opt.value}
                            className="mr-2"
                          />
                          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Notes
                    </label>
                    <textarea
                      id="editComponentNotes"
                      rows={3}
                      defaultValue={selectedComponent.notes || ''}
                      className={`w-full p-2 border rounded-none text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                      placeholder="Additional notes about this component..."
                    />
                  </div>
                </div>

              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowComponentEditModal(false);
                    setSelectedComponent(null);
                  }}
                  className={`px-4 py-2 rounded-none text-sm font-medium ${
                    darkMode ? 'bg-gray-600 text-white hover:bg-gray-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  } transition-colors`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const name = document.getElementById('editComponentName').value.trim();
                    const categoryRadio = document.querySelector('input[name="editComponentCategory"]:checked');
                    const category = categoryRadio ? categoryRadio.value : '';
                    const notes = document.getElementById('editComponentNotes').value.trim();

                    // Validation
                    if (!name || !category) {
                      toast.error('Please fill in all required fields');
                      return;
                    }

                    const updatedComponent = {
                      ...selectedComponent,
                      name,
                      category,
                      notes
                    };

                    updateComponent(selectedComponent.id, updatedComponent);
                    setShowComponentEditModal(false);
                    setSelectedComponent(null);
                    toast.success('Component updated successfully!');
                  }}
                  className={`px-4 py-2 rounded-none text-sm font-medium ${
                    darkMode ? 'bg-redhalo-navy text-white hover:bg-redhalo-navy-light' : 'bg-redhalo-red text-white hover:bg-redhalo-red-dark'
                  } transition-colors`}
                >
                  Update Component
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  
    // Statutory Tab
    const renderStatutoryTab = () => (
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-md`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Statutory Configuration
          </h2>
          <button
            onClick={() => setShowStatutoryModal(true)}
            className={`px-4 py-2 rounded text-sm font-medium ${
              darkMode ? 'bg-redhalo-navy text-white hover:bg-redhalo-navy-light' : 'bg-redhalo-red text-white hover:bg-redhalo-red-dark'
            } transition-colors`}
          >
            Add Statutory Config
          </button>
        </div>
  
        <div className="space-y-4">
          {statutoryConfigs.map(config => (
            <div key={config.id} className={`p-4 rounded-lg border ${darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'}`}>
              <div className="flex justify-between items-start">
                <div>
                  <h3 className={`text-md font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    {config.name}
                  </h3>
                  <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mt-2`}>
                    <div>PF: Employee {config.pf.employeePct}% | Employer {config.pf.employerPct}% | Ceiling ₹{config.pf.wageCeiling?.toLocaleString()}</div>
                    <div>ESI: Employee {config.esi.employeePct}% | Employer {config.esi.employerPct}% | Eligibility ₹{config.esi.eligibilityGrossMonthly?.toLocaleString()}</div>
                    <div>Gratuity: {config.gratuity.ratePct}% of {config.gratuity.basis}</div>
                    {config.effectiveFrom && (
                      <div>Effective From: {formatDate(config.effectiveFrom)}</div>
                    )}
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setSelectedStatutoryConfig(config);
                      setShowStatutoryModal(true);
                    }}
                    className={`px-3 py-1 rounded text-xs font-medium ${
                      darkMode ? 'bg-redhalo-navy text-white hover:bg-redhalo-navy-light' : 'bg-redhalo-red text-white hover:bg-redhalo-red-dark'
                    } transition-colors`}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => deleteStatutoryConfig(config.id)}
                    className={`px-3 py-1 rounded text-xs font-medium ${
                      darkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-500 text-white hover:bg-red-600'
                    } transition-colors`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
  
          {statutoryConfigs.length === 0 && (
            <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No statutory configurations created yet. Click "Add Statutory Config" to get started.
            </div>
          )}
        </div>
  
        {/* Statutory Configuration Modal */}
        {showStatutoryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className={`p-6 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`}>
              <div className="flex justify-between items-center mb-4">
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  {selectedStatutoryConfig ? 'Edit' : 'Create'} Statutory Configuration
                </h3>
                <button
                  onClick={() => {
                    setShowStatutoryModal(false);
                    setSelectedStatutoryConfig(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              </div>
  
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <h4 className={`text-md font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    Basic Information
                  </h4>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Configuration Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="statutoryName"
                      defaultValue={selectedStatutoryConfig?.name || ''}
                      className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                      placeholder="e.g., Default Statutory Config"
                    />
                  </div>
  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Effective From <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      id="statutoryEffectiveFrom"
                      defaultValue={selectedStatutoryConfig?.effectiveFrom || ''}
                      className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>
                </div>
  
                {/* PF Configuration */}
                <div className="space-y-4">
                  <h4 className={`text-md font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    Provident Fund (PF)
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Employee Share (%) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        id="pfEmployeePct"
                        defaultValue={selectedStatutoryConfig?.pf?.employeePct || 12}
                        className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Employer Share (%) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        id="pfEmployerPct"
                        defaultValue={selectedStatutoryConfig?.pf?.employerPct || 12}
                        className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                      />
                    </div>
                  </div>
  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Wage Ceiling (₹) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="pfWageCeiling"
                      defaultValue={selectedStatutoryConfig?.pf?.wageCeiling || 15000}
                      className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>
  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="pfApplyCeiling"
                      defaultChecked={selectedStatutoryConfig?.pf?.applyCeiling !== false}
                      className="mr-2"
                    />
                    <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Apply ceiling limit
                    </span>
                  </div>
                </div>
  
                {/* ESI Configuration */}
                <div className="space-y-4">
                  <h4 className={`text-md font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    Employee State Insurance (ESI)
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Employee Share (%) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        id="esiEmployeePct"
                        defaultValue={selectedStatutoryConfig?.esi?.employeePct || 0.75}
                        className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Employer Share (%) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        id="esiEmployerPct"
                        defaultValue={selectedStatutoryConfig?.esi?.employerPct || 3.25}
                        className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                      />
                    </div>
                  </div>
  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Eligibility Gross Monthly (₹) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="esiEligibilityGross"
                      defaultValue={selectedStatutoryConfig?.esi?.eligibilityGrossMonthly || 21000}
                      className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>
                </div>
  
                {/* Gratuity Configuration */}
                <div className="space-y-4">
                  <h4 className={`text-md font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    Gratuity
                  </h4>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Calculation Basis <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="gratuityBasis"
                      defaultValue={selectedStatutoryConfig?.gratuity?.basis || 'BASIC'}
                      className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    >
                      <option value="BASIC">Basic Salary</option>
                      <option value="BASIC_DA">Basic + DA</option>
                      <option value="GROSS">Gross Salary</option>
                    </select>
                  </div>
  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Rate (%) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      id="gratuityRate"
                      defaultValue={selectedStatutoryConfig?.gratuity?.ratePct || 4.81}
                      className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>
                </div>
              </div>
  
              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowStatutoryModal(false);
                    setSelectedStatutoryConfig(null);
                  }}
                  className={`px-4 py-2 rounded text-sm font-medium ${
                    darkMode ? 'bg-gray-600 text-white hover:bg-gray-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  } transition-colors`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const name = document.getElementById('statutoryName').value.trim();
                    const effectiveFrom = document.getElementById('statutoryEffectiveFrom').value;
                    const pfEmployeePct = parseFloat(document.getElementById('pfEmployeePct').value);
                    const pfEmployerPct = parseFloat(document.getElementById('pfEmployerPct').value);
                    const pfWageCeiling = parseFloat(document.getElementById('pfWageCeiling').value);
                    const pfApplyCeiling = document.getElementById('pfApplyCeiling').checked;
                    const esiEmployeePct = parseFloat(document.getElementById('esiEmployeePct').value);
                    const esiEmployerPct = parseFloat(document.getElementById('esiEmployerPct').value);
                    const esiEligibilityGross = parseFloat(document.getElementById('esiEligibilityGross').value);
                    const gratuityBasis = document.getElementById('gratuityBasis').value;
                    const gratuityRate = parseFloat(document.getElementById('gratuityRate').value);
  
                    if (!name || !effectiveFrom) {
                      toast.error('Please fill in all required fields');
                      return;
                    }
  
                    const newConfig = {
                      id: selectedStatutoryConfig?.id || Date.now(),
                      name,
                      effectiveFrom,
                      pf: {
                        employeePct: pfEmployeePct,
                        employerPct: pfEmployerPct,
                        wageCeiling: pfWageCeiling,
                        applyCeiling: pfApplyCeiling
                      },
                      esi: {
                        employeePct: esiEmployeePct,
                        employerPct: esiEmployerPct,
                        eligibilityGrossMonthly: esiEligibilityGross
                      },
                      gratuity: {
                        basis: gratuityBasis,
                        ratePct: gratuityRate
                      }
                    };
  
                    if (selectedStatutoryConfig) {
                      // Update existing config
                      const updatedConfigs = statutoryConfigs.map(config => 
                        config.id === selectedStatutoryConfig.id ? newConfig : config
                      );
                      saveStatutoryConfigs(updatedConfigs);
                      toast.success('Statutory configuration updated successfully!');
                    } else {
                      // Create new config
                      saveStatutoryConfigs([...statutoryConfigs, newConfig]);
                      toast.success('Statutory configuration created successfully!');
                    }
  
                    setShowStatutoryModal(false);
                    setSelectedStatutoryConfig(null);
                  }}
                  className={`px-4 py-2 rounded text-sm font-medium ${
                    darkMode ? 'bg-redhalo-navy text-white hover:bg-redhalo-navy-light' : 'bg-redhalo-red text-white hover:bg-redhalo-red-dark'
                  } transition-colors`}
                >
                  {selectedStatutoryConfig ? 'Update' : 'Create'} Configuration
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  
    // Simulator Tab
    const renderSimulatorTab = () => (
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-md`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Salary Simulator
          </h2>
        </div>
  
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Target Annual (₹)
            </label>
            <input
              type="number"
              value={simulatorData.targetAnnual}
              onChange={(e) => setSimulatorData({ ...simulatorData, targetAnnual: e.target.value })}
              className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
              placeholder="Enter target annual amount"
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Mode
            </label>
            <select
              value={simulatorData.mode}
              onChange={(e) => setSimulatorData({ ...simulatorData, mode: e.target.value })}
              className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
            >
              <option value="CTC">CTC Mode</option>
              <option value="GROSS">Gross Mode</option>
            </select>
          </div>
        </div>
  
        <button
          onClick={() => {
            if (!simulatorData.targetAnnual) {
              toast.error('Please enter target annual amount');
              return;
            }
            toast.success('Simulation completed');
          }}
          className={`px-4 py-2 rounded text-sm font-medium ${
            darkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-500 text-white hover:bg-green-600'
          } transition-colors`}
        >
          Compute
        </button>
      </div>
    );
  
    // Compliance Tab
    const renderComplianceTab = () => (
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-md`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Compliance Report
          </h2>
        </div>
  
        <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          No compliance issues found. Run a simulation to check compliance.
        </div>
      </div>
    );
  
    // Versions Tab
    const renderVersionsTab = () => (
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-md`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Version History
          </h2>
        </div>
  
        <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          No versions saved yet. Create and save versions to track changes.
        </div>
      </div>
    );
  
    // Salary Structures Tab
    const renderStructuresTab = () => (
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-md`}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Side - Actions */}
          <div className="lg:col-span-1">
            <div className="space-y-4">
              {/* Create Salary Structure Button */}
              <div className={`p-4 rounded-lg border ${darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'}`}>
                <h3 className={`text-base font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Salary Structure Actions
                </h3>
                <button
                  onClick={addStructure}
                  className={`w-full px-4 py-2 rounded text-sm font-medium ${
                    darkMode ? 'bg-redhalo-navy text-white hover:bg-redhalo-navy-light' : 'bg-redhalo-red text-white hover:bg-redhalo-red-dark'
                  } transition-colors`}
                >
                  Create Salary Structure
                </button>
              </div>
  
              {/* Formula Builder Button */}
              <div className={`p-4 rounded-lg border ${darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'}`}>
                <h3 className={`text-base font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Formula Builder
                </h3>
                <button
                  onClick={() => setShowFormulaManager(true)}
                  className={`w-full px-4 py-2 rounded text-sm font-medium ${
                    darkMode ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-purple-500 text-white hover:bg-purple-600'
                  } transition-colors`}
                >
                  Open Formula Builder
                </button>
              </div>
            </div>
          </div>
  
          {/* Right Side - Components Grid */}
          <div className="lg:col-span-2">
            <div className={`p-4 rounded-lg border ${darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'}`}>
              <div className="flex justify-between items-center mb-4">
                <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Salary Components
                </h3>
                <button
                  onClick={() => setShowComponentModal(true)}
                  className={`p-2 rounded-full text-sm font-medium ${
                    darkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-500 text-white hover:bg-green-600'
                  } transition-colors`}
                  title="Add Component"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>
  
              {/* Components Grid */}
              <div className="max-h-96 overflow-y-auto pr-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {components.length === 0 ? (
                    <div className={`col-span-2 text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      No components created yet. Click the + button to create your first component.
                    </div>
                  ) : (
                    components.map(component => (
                      <div key={component.id} className={`p-3 rounded-lg border ${darkMode ? 'border-gray-600 bg-gray-600' : 'border-gray-300 bg-white'} hover:shadow-md transition-shadow`}>
                      {/* Component Header */}
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <h4 className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                            {component.name || 'Unnamed Component'}
                          </h4>
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Code: {component.code || 'N/A'}
                          </p>
                        </div>
                        <div className="relative component-dropdown">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdownId(openDropdownId === component.id ? null : component.id);
                            }}
                            className={`p-1 rounded ${darkMode ? 'hover:bg-gray-500' : 'hover:bg-gray-200'} transition-colors`}
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                            </svg>
                          </button>
                          {/* Dropdown Menu */}
                          {openDropdownId === component.id && (
                            <div className={`absolute right-0 mt-1 w-32 rounded-md shadow-lg z-10 ${darkMode ? 'bg-gray-600' : 'bg-white'} border ${darkMode ? 'border-gray-500' : 'border-gray-200'}`}>
                              <div className="py-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenDropdownId(null);
                                    setSelectedComponent(component);
                                    setShowComponentEditModal(true);
                                  }}
                                  className={`block w-full text-left px-4 py-2 text-sm ${darkMode ? 'text-gray-300 hover:bg-gray-500' : 'text-gray-700 hover:bg-gray-100'}`}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenDropdownId(null);
                                    updateComponent(component.id, { isActive: !component.isActive });
                                  }}
                                  className={`block w-full text-left px-4 py-2 text-sm ${darkMode ? 'text-gray-300 hover:bg-gray-500' : 'text-gray-700 hover:bg-gray-100'}`}
                                >
                                  {component.isActive ? 'Deactivate' : 'Activate'}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenDropdownId(null);
                                    deleteComponent(component.id);
                                  }}
                                  className={`block w-full text-left px-4 py-2 text-sm ${darkMode ? 'text-red-400 hover:bg-gray-500' : 'text-red-600 hover:bg-gray-100'}`}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
  
                      {/* Component Badges */}
                      <div className="space-y-1">
                        <div className="flex flex-wrap gap-1">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            component.category === 'EARNING' ? (darkMode ? 'bg-green-600 text-white' : 'bg-green-100 text-green-800') :
                            component.category === 'DEDUCTION' ? (darkMode ? 'bg-red-600 text-white' : 'bg-red-100 text-red-800') :
                            (darkMode ? 'bg-redhalo-navy text-white' : 'rh-accent-soft')
                          }`}>
                            {component.category}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            component.flags?.taxable === 'TAXABLE' ? (darkMode ? 'bg-red-600 text-white' : 'bg-red-100 text-red-800') :
                            component.flags?.taxable === 'EXEMPT' ? (darkMode ? 'bg-green-600 text-white' : 'bg-green-100 text-green-800') :
                            (darkMode ? 'bg-yellow-600 text-white' : 'bg-yellow-100 text-yellow-800')
                          }`}>
                            {component.flags?.taxable || 'TAXABLE'}
                          </span>
                        </div>
  
                        {/* Statutory Applicability */}
                        <div className="flex flex-wrap gap-1">
                          {component.flags?.pfApplicable && (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-redhalo-navy text-white' : 'rh-accent-soft'}`}>
                              PF
                            </span>
                          )}
                          {component.flags?.esiApplicable && (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-pink-600 text-white' : 'bg-pink-100 text-pink-800'}`}>
                              ESI
                            </span>
                          )}
                          {component.flags?.gratuityApplicable && (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-orange-600 text-white' : 'bg-orange-100 text-orange-800'}`}>
                              Gratuity
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                </div>
              </div>
            </div>
          </div>
        </div>
  
        {/* Structure Editor Modal */}
        {selectedStructure && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className={`p-6 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`}>
              <div className="flex justify-between items-center mb-4">
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Edit Salary Structure: {selectedStructure.title}
                </h3>
                <button
                  onClick={() => setSelectedStructure(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              </div>
  
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Structure Details */}
                <div className="space-y-4">
                  <h4 className={`text-md font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    Structure Details
                  </h4>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Structure Name
                    </label>
                    <input
                      type="text"
                      value={selectedStructure.title || ''}
                      onChange={(e) => updateStructure(selectedStructure.id, { title: e.target.value })}
                      className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                      placeholder="Enter structure name"
                    />
                  </div>
  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Target Annual (₹)
                    </label>
                    <input
                      type="number"
                      value={selectedStructure.targetAnnual || ''}
                      onChange={(e) => updateStructure(selectedStructure.id, { targetAnnual: parseFloat(e.target.value) })}
                      className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                      placeholder="Enter target annual amount"
                    />
                  </div>
  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Mode
                    </label>
                    <select
                      value={selectedStructure.mode || 'CTC'}
                      onChange={(e) => updateStructure(selectedStructure.id, { mode: e.target.value })}
                      className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    >
                      <option value="CTC">CTC Mode</option>
                      <option value="GROSS">Gross Mode</option>
                    </select>
                  </div>
  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Effective From
                    </label>
                    <input
                      type="date"
                      value={selectedStructure.effectiveFrom || ''}
                      onChange={(e) => updateStructure(selectedStructure.id, { effectiveFrom: e.target.value })}
                      className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>
                </div>
  
                {/* Component Selection */}
                <div className="space-y-4">
                  <h4 className={`text-md font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    Add Components
                  </h4>
                  
                  <div className={`p-3 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <p className={`text-sm mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Select components to add to this structure:
                    </p>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {components.filter(comp => comp.isActive).map(component => {
                        const isAdded = selectedStructure.resolvedComponents?.some(c => c.id === component.id);
                        return (
                          <div key={component.id} className="flex items-center justify-between p-2 border rounded">
                            <div>
                              <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                                {component.name}
                              </span>
                              <span className={`text-xs ml-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                ({component.category})
                              </span>
                            </div>
                            <button
                              onClick={() => {
                                if (isAdded) {
                                  // Remove component
                                  const updatedComponents = selectedStructure.resolvedComponents.filter(c => c.id !== component.id);
                                  updateStructure(selectedStructure.id, { resolvedComponents: updatedComponents });
                                } else {
                                  // Add component
                                  const newComponent = {
                                    ...component,
                                    ordering: (selectedStructure.resolvedComponents?.length || 0) + 1
                                  };
                                  const updatedComponents = [...(selectedStructure.resolvedComponents || []), newComponent];
                                  updateStructure(selectedStructure.id, { resolvedComponents: updatedComponents });
                                }
                              }}
                              className={`px-3 py-1 rounded text-xs font-medium ${
                                isAdded
                                  ? (darkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-500 text-white hover:bg-red-600')
                                  : (darkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-500 text-white hover:bg-green-600')
                              } transition-colors`}
                            >
                              {isAdded ? 'Remove' : 'Add'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
  
                  {/* Calculate Button */}
                  <button
                    onClick={() => {
                      if (selectedStructure.resolvedComponents && selectedStructure.resolvedComponents.length > 0) {
                        const result = calculateSalary(selectedStructure, selectedStructure.targetAnnual || 0);
                        updateStructure(selectedStructure.id, { computedSummary: result.summary });
                        toast.success('Salary structure calculated successfully!');
                      } else {
                        toast.error('Please add components before calculating');
                      }
                    }}
                    className={`w-full px-4 py-2 rounded text-sm font-medium ${
                      darkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-500 text-white hover:bg-green-600'
                    } transition-colors`}
                  >
                    Calculate Structure
                  </button>
                </div>
              </div>
  
              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setSelectedStructure(null)}
                  className={`px-4 py-2 rounded text-sm font-medium ${
                    darkMode ? 'bg-gray-600 text-white hover:bg-gray-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  } transition-colors`}
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    toast.success('Structure saved successfully!');
                    setSelectedStructure(null);
                  }}
                  className={`px-4 py-2 rounded text-sm font-medium ${
                    darkMode ? 'bg-redhalo-navy text-white hover:bg-redhalo-navy-light' : 'bg-redhalo-red text-white hover:bg-redhalo-red-dark'
                  } transition-colors`}
                >
                  Save Structure
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  
    // Structure Management Tab (Legacy - keeping for compatibility)
    const renderStructureManagement = () => (
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-md`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Structure Management
          </h2>
          <button
            onClick={addStructure}
            className={`px-4 py-2 rounded text-sm font-medium ${
              darkMode ? 'bg-redhalo-navy text-white hover:bg-redhalo-navy-light' : 'bg-redhalo-red text-white hover:bg-redhalo-red-dark'
            } transition-colors`}
          >
            Add Structure
          </button>
        </div>
  
        <div className="space-y-4">
          {structures.map(structure => (
            <div key={structure.id} className={`p-4 rounded-lg border ${darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <div>
                  <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Structure Name
                  </label>
                  <input
                    type="text"
                    value={structure.title}
                    onChange={(e) => updateStructure(structure.id, { title: e.target.value })}
                    className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    placeholder="e.g., Standard Salary Structure"
                  />
                </div>
  
                <div>
                  <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Target Annual
                  </label>
                  <input
                    type="number"
                    value={structure.targetAnnual}
                    onChange={(e) => updateStructure(structure.id, { targetAnnual: parseFloat(e.target.value) })}
                    className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    placeholder="Annual target amount"
                  />
                </div>
              </div>
  
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <div>
                  <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Mode
                  </label>
                  <select
                    value={structure.mode}
                    onChange={(e) => updateStructure(structure.id, { mode: e.target.value })}
                    className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                  >
                    <option value="CTC">CTC Mode</option>
                    <option value="GROSS">Gross Mode</option>
                  </select>
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Effective From
                  </label>
                  <input
                    type="date"
                    value={structure.effectiveFrom}
                    onChange={(e) => updateStructure(structure.id, { effectiveFrom: e.target.value })}
                    className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>
              </div>
  
              <div className="flex justify-between items-center">
                               <button
                   onClick={() => {
                     // Save version logic
                     toast.success('Version saved');
                   }}
                  className={`px-3 py-1 rounded text-sm font-medium ${
                    darkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-500 text-white hover:bg-green-600'
                  } transition-colors`}
                >
                  Save Version
                </button>
                <button
                  onClick={() => deleteStructure(structure.id)}
                  className={`px-3 py-1 rounded text-sm font-medium ${
                    darkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-500 text-white hover:bg-red-600'
                  } transition-colors`}
                >
                  Delete Structure
                </button>
              </div>
            </div>
          ))}
  
          {structures.length === 0 && (
            <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No structures created yet. Click "Add Structure" to get started.
            </div>
          )}
        </div>
      </div>
    );
  
  
  
    return (
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-md`}>
        {/* Tab Navigation */}
        <div className="flex border-b border-gray-300 mb-2 overflow-x-auto">
          {[
            { id: 'components', label: 'Component Manager', icon: '🔧' },
            { id: 'structures', label: 'Salary Structures', icon: '📊' },
            { id: 'statutory', label: 'Statutory Rates', icon: '⚖️' },
            { id: 'simulator', label: 'Salary Simulator', icon: '🧪' }
          ].map(tab => (
            <button
              key={tab.id}
              className={`px-3 py-1 text-sm font-medium transition-colors flex items-center space-x-1 whitespace-nowrap ${
                activeTab === tab.id 
                  ? `${darkMode ? 'border-b-2 border-redhalo-red text-redhalo-mint' : 'border-b-2 border-redhalo-red text-redhalo-red'}`
                  : `${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'}`
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
  
        {/* Tab Content */}
        {activeTab === 'components' && renderComponentsTab()}
        {activeTab === 'structures' && (
          <SalaryStructureManager 
            salaryComponents={components.map(comp => ({
              id: comp.id,
              name: comp.name,
              code: comp.code,
              active: comp.isActive !== false,
              categories: {
                earning: comp.category === 'EARNING',
                deduction: comp.category === 'DEDUCTION',
                benefit: comp.category === 'BENEFIT',
                reimbursement: comp.category === 'REIMBURSEMENT'
              },
              cumulative: 'no' // Default value, can be updated if needed
            }))}
            onComponentUpdate={() => {
              // Reload components from localStorage to get latest updates
              const savedComponents = localStorage.getItem('salaryComponents');
              if (savedComponents) {
                setComponents(JSON.parse(savedComponents));
              }
            }}
            organizationalLevels={{}}
            statutoryRates={{
              PF_RATE: 12,
              ESI_RATE: 1.75,
              GRATUITY_RATE: 4.81,
              TDS_THRESHOLD: 250000,
              MAX_PF_LIMIT: 15000
            }}
          />
        )}
        {activeTab === 'statutory' && <StatutoryRatesManager />}
        {activeTab === 'simulator' && (
          <SalaryStructureSimulator 
            salaryComponents={[]}
            salaryStructures={[]}
          />
        )}
  
        {/* Formula Builder Modal */}
        <FormulaBuilder
          isOpen={showFormulaBuilder && selectedComponent?.calculationType === 'FORMULA'}
          onClose={() => setShowFormulaBuilder(false)}
          component={selectedComponent}
          onSave={(valueExpression) => {
            if (selectedComponent) {
              updateComponent(selectedComponent.id, { valueExpression });
            }
            setShowFormulaBuilder(false);
            setSelectedComponent(null);
          }}
        />
  
        {/* Formula Manager Modal */}
        {showFormulaManager && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className={`p-6 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`}>
              <div className="flex justify-between items-center mb-4">
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Advanced Formula Manager
                </h3>
                <button
                  onClick={() => setShowFormulaManager(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              </div>
  
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Side - Formula Builder */}
                <div className={`p-4 rounded-lg border ${darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'}`}>
                  <h4 className={`text-base font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    Formula Builder
                  </h4>
                  
                  {/* Available Components */}
                  <div className="mb-4">
                    <h5 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Available Components & Variables
                    </h5>
                    <div className="flex flex-wrap gap-2">
                      {/* Default Components */}
                      <button
                        onClick={() => addToFormula('BASIC')}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          darkMode ? 'bg-redhalo-navy text-white hover:bg-redhalo-navy-light' : 'rh-accent-soft hover:bg-redhalo-mint'
                        } transition-colors`}
                      >
                        BASIC
                      </button>
                      <button
                        onClick={() => addToFormula('DA')}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          darkMode ? 'bg-redhalo-navy text-white hover:bg-redhalo-navy-light' : 'rh-accent-soft hover:bg-redhalo-mint'
                        } transition-colors`}
                      >
                        DA
                      </button>
                      <button
                        onClick={() => addToFormula('HRA')}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          darkMode ? 'bg-redhalo-navy text-white hover:bg-redhalo-navy-light' : 'rh-accent-soft hover:bg-redhalo-mint'
                        } transition-colors`}
                      >
                        HRA
                      </button>
                      <button
                        onClick={() => addToFormula('GROSS_EARNINGS')}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          darkMode ? 'bg-redhalo-navy text-white hover:bg-redhalo-navy-light' : 'rh-accent-soft hover:bg-redhalo-mint'
                        } transition-colors`}
                      >
                        GROSS_EARNINGS
                      </button>
                      <button
                        onClick={() => addToFormula('CTC')}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          darkMode ? 'bg-redhalo-navy text-white hover:bg-redhalo-navy-light' : 'rh-accent-soft hover:bg-redhalo-mint'
                        } transition-colors`}
                      >
                        CTC
                      </button>
                      
                      {/* Dynamic Components */}
                      {components.map(comp => (
                        <button
                          key={comp.id}
                          onClick={() => addToFormula(comp.code || comp.name)}
                          className={`px-3 py-1 rounded text-xs font-medium ${
                            darkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-100 text-green-800 hover:bg-green-200'
                          } transition-colors`}
                        >
                          {comp.code || comp.name}
                        </button>
                      ))}
                    </div>
                  </div>
  
                  {/* Operators */}
                  <div className="mb-4">
                    <h5 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Operators
                    </h5>
                    <div className="flex flex-wrap gap-2">
                      {['+', '-', '*', '/', '(', ')', 'Math.round', 'Math.min', 'Math.max'].map(op => (
                        <button
                          key={op}
                          onClick={() => addToFormula(op)}
                          className={`px-3 py-1 rounded text-xs font-medium ${
                            darkMode ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                          } transition-colors`}
                        >
                          {op}
                        </button>
                      ))}
                    </div>
                  </div>
  
                  {/* Formula Input */}
                  <div className="mb-4">
                    <h5 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Formula
                    </h5>
                    <textarea
                      value={selectedFormula?.formula || ''}
                      onChange={(e) => setSelectedFormula(prev => ({ ...prev, formula: e.target.value }))}
                      placeholder="Enter your formula here..."
                      rows={4}
                      className={`w-full p-3 border rounded text-sm font-mono ${
                        darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
                      }`}
                    />
                  </div>
  
                  {/* Formula Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={verifyFormula}
                      className={`px-4 py-2 rounded text-sm font-medium ${
                        darkMode ? 'bg-yellow-600 text-white hover:bg-yellow-700' : 'bg-yellow-500 text-white hover:bg-yellow-600'
                      } transition-colors`}
                    >
                      Verify Formula
                    </button>
                    <button
                      onClick={saveFormula}
                      className={`px-4 py-2 rounded text-sm font-medium ${
                        darkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-500 text-white hover:bg-green-600'
                      } transition-colors`}
                    >
                      Save Formula
                    </button>
                  </div>
  
                  {/* Verification Result */}
                  {verificationResult && (
                    <div className={`mt-3 p-3 rounded text-sm ${
                      verificationResult.valid 
                        ? (darkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800')
                        : (darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800')
                    }`}>
                      {verificationResult.valid ? verificationResult.message : verificationResult.error}
                    </div>
                  )}
                </div>
  
                {/* Right Side - Saved Formulas List */}
                <div className={`p-4 rounded-lg border ${darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'}`}>
                  <h4 className={`text-base font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    Saved Formulas
                  </h4>
                  
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {savedFormulas.length === 0 ? (
                      <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        No formulas saved yet. Create your first formula using the builder.
                      </div>
                    ) : (
                      savedFormulas.map(formula => (
                        <div
                          key={formula.id}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedFormula?.id === formula.id
                              ? (darkMode ? 'border-redhalo-red bg-redhalo-navy' : 'border-redhalo-red bg-redhalo-mint/50')
                              : (darkMode ? 'border-gray-600 bg-gray-600 hover:bg-gray-500' : 'border-gray-300 bg-white hover:bg-gray-50')
                          }`}
                          onClick={() => setSelectedFormula(formula)}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h5 className={`font-medium text-sm ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                                {formula.name}
                              </h5>
                              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                Code: {formula.code}
                              </p>
                              <p className={`text-xs font-mono mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                {formula.formula}
                              </p>
                            </div>
                            <div className="flex gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedFormula(formula);
                                }}
                                className={`p-1 rounded text-xs ${
                                  darkMode ? 'text-redhalo-mint hover:bg-gray-500' : 'text-redhalo-navy hover:bg-gray-100'
                                }`}
                              >
                                Edit
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteFormula(formula.id);
                                }}
                                className={`p-1 rounded text-xs ${
                                  darkMode ? 'text-red-400 hover:bg-gray-500' : 'text-red-600 hover:bg-gray-100'
                                }`}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

export default EnhancedSalaryStructureManager;